/**
 * useChangePassword — Layer-3 hook for changing the authenticated user's password.
 *
 * Flow: POST /auth/change-password/ with old + new password values.
 * `submit` matches ChangePasswordForm.onSubmit exactly — pass it directly as the prop.
 */
import { useMutation } from "@tanstack/react-query";
import { changePassword } from "../api/authApi";
import { ApiError } from "@/shared/lib/axios";

export function useChangePassword() {
  const mutation = useMutation({
    mutationFn: (creds: {
      oldPassword: string;
      newPassword: string;
      newPasswordConfirm: string;
    }) =>
      changePassword(creds.oldPassword, creds.newPassword, creds.newPasswordConfirm),
  });

  return {
    ...mutation,
    isChanging: mutation.isPending,
    error: (mutation.error as ApiError | null) ?? null,
    /** Matches ChangePasswordForm.onSubmit — pass directly as the prop. */
    submit: (oldPassword: string, newPassword: string, newPasswordConfirm: string) =>
      mutation.mutateAsync({ oldPassword, newPassword, newPasswordConfirm }),
  };
}
